export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

const buildUrl = (path, params) => {
  const url = new URL(path, API_BASE_URL);

  if (params) {
    const searchParams = params instanceof URLSearchParams ? params : new URLSearchParams();

    if (!(params instanceof URLSearchParams)) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          searchParams.append(key, String(value));
        }
      });
    }

    const queryString = searchParams.toString();
    if (queryString) {
      url.search = queryString;
    }
  }

  return url.toString();
};

const parseResponseBody = async (response) => {
  if (response.status === 204) {
    return null;
  }

  const rawText = await response.text();
  if (!rawText) {
    return null;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(rawText);
    } catch (error) {
      return rawText;
    }
  }

  try {
    return JSON.parse(rawText);
  } catch (error) {
    return rawText;
  }
};

export const apiRequest = async (
  path,
  { method = "GET", data, params, headers = {}, auth = true, timeoutMs } = {}
) => {
  const url = buildUrl(path, params);
  const requestHeaders = new Headers(headers);

  if (auth) {
    const token = localStorage.getItem("token");
    if (token && !requestHeaders.has("Authorization")) {
      requestHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  let body;
  if (method !== "GET" && method !== "HEAD" && data !== undefined && data !== null) {
    if (data instanceof FormData || data instanceof Blob || data instanceof ArrayBuffer) {
      body = data;
    } else if (data instanceof URLSearchParams) {
      body = data;
      if (!requestHeaders.has("Content-Type")) {
        requestHeaders.set("Content-Type", "application/x-www-form-urlencoded;charset=UTF-8");
      }
    } else if (typeof data === "string") {
      body = data;
      if (!requestHeaders.has("Content-Type")) {
        requestHeaders.set("Content-Type", "text/plain;charset=UTF-8");
      }
    } else {
      body = JSON.stringify(data);
      if (!requestHeaders.has("Content-Type")) {
        requestHeaders.set("Content-Type", "application/json");
      }
    }
  }

  const controller = timeoutMs ? new AbortController() : null;
  const timeoutId = timeoutMs ? setTimeout(() => controller?.abort(), timeoutMs) : null;

  try {
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body,
      signal: controller?.signal,
    });

    const parsedData = await parseResponseBody(response);
    const result = {
      data: parsedData,
      status: response.status,
      ok: response.ok,
      headers: response.headers,
    };

    if (!response.ok) {
      const error = new Error(
        (parsedData && parsedData.message) || response.statusText || `Request failed with status ${response.status}`
      );
      error.status = response.status;
      error.data = parsedData;
      error.response = { status: response.status, data: parsedData };
      throw error;
    }

    return result;
  } catch (error) {
    if (error?.name === "AbortError" && timeoutMs) {
      const timeoutError = new Error(`Request timed out after ${timeoutMs}ms`);
      timeoutError.name = "AbortError";
      timeoutError.code = "ECONNABORTED";
      throw timeoutError;
    }

    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

export const apiGet = (path, options = {}) => apiRequest(path, { ...options, method: "GET" });
export const apiPost = (path, data, options = {}) => apiRequest(path, { ...options, method: "POST", data });
export const apiPut = (path, data, options = {}) => apiRequest(path, { ...options, method: "PUT", data });
export const apiPatch = (path, data, options = {}) => apiRequest(path, { ...options, method: "PATCH", data });
export const apiDelete = (path, options = {}) => apiRequest(path, { ...options, method: "DELETE" });

// ------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

export const getBooks = async () => {
  try {
    const response = await apiGet(`/api/books`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getBooks", error);
    return false;
  }
};

export const getRecentBooks = async () => {
    try {
        const response = await apiGet(`/api/books/recent`);
        return response.data;
    } catch (error) {
        console.error("Ошибка в getRecentBooks", error);
        return false;
    }
};


export const postBooks = async ({ formData }) => {
  try {
    const response = await apiPost(`/api/books`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в postBooks", error);
    return false;
  }
};

export const patchBook = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/books/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchBook", error);
    return false;
  }
};

export const deleteBook = async (id) => {
  try {
    const response = await apiDelete(`/api/books/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteBook", error);
    return false;
  }
};

export const getBookById = async (id) => {
  try {
    const response = await apiGet(`/api/books/${id}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getBookById", error);
    return false;
  }
};

// ---------------------------------------------------------------------------------------------------------

export const fetchLogin = async (email, password, libraryCard) => {
  try {
    const loginData = { password };
    if (email) loginData.email = email;
    if (libraryCard) loginData.libraryCard = libraryCard;

    const response = await apiPost("/api/auth/signin", loginData);
    return response.data;
  } catch (error) {
    console.error("API Error:fetchLogin", error);
    throw error;
  }
};

export const fetchRegisterAdmin = async (formData) => {
  try {
    const response = await apiPost("/api/auth/signup", formData);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};

export const fetchRegisterUser = async (formData) => {
  try {
    const response = await apiPost("/api/auth/user/signup", formData);
    return response.data;
  } catch (error) {
    console.error("API Error:", error);
    throw error;
  }
};


// Профиль пользователя
export const getProfile = async () => {
  try {
    const response = await apiGet('/api/profile');
    return response.data;
  } catch (error) {
    console.error('Ошибка получения профиля:', error);
    throw error;
  }
};

export const updateProfile = async (profileData) => {
  try {
    const response = await apiPut('/api/profile', profileData);
    return response.data;
  } catch (error) {
    console.error('Ошибка обновления профиля:', error);
    throw error;
  }
};


// Управление пользователями
export const getUsers = async () => {
  try {
    const response = await apiGet("/api/users");
    return response.data;
  } catch (error) {
    console.error("Ошибка получения пользователей:", error);
    throw error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await apiPost("/api/users", userData);
    return response.data;
  } catch (error) {
    console.error("Ошибка создания пользователя:", error);
    throw error;
  }
};

export const updateUser = async (id, userData) => {
  try {
    const response = await apiPatch(`/api/users/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error("Ошибка обновления пользователя:", error);
    throw error;
  }
};

export const deleteUser = async (id) => {
  try {
    const response = await apiDelete(`/api/users/${id}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка удаления пользователя:", error);
    throw error;
  }
};

// Управление администраторами
export const getAdmins = async () => {
  try {
    const response = await apiGet("/api/admins");
    return response.data;
  } catch (error) {
    console.error("Ошибка получения администраторов:", error);
    throw error;
  }
};

export const updateAdmin = async (id, adminData) => {
  try {
    const response = await apiPatch(`/api/admins/${id}`, adminData);
    return response.data;
  } catch (error) {
    console.error("Ошибка обновления администратора:", error);
    throw error;
  }
};

export const deleteAdmin = async (id) => {
  try {
    const response = await apiDelete(`/api/admins/${id}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка удаления администратора:", error);
    throw error;
  }
};

// ---------------------------------------------------------------------------------------------------------

export const getQuestion = async () => {
  try {
    const response = await apiGet(`/api/question`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getQuestion", error);
    return false;
  }
};

export const QuestionTrue = async ({ typeId, query }) => {
  try {
    const params = new URLSearchParams();
    if (typeId) params.append("typeId", typeId);
    if (query) params.append("query", query);

    const response = await apiGet(`/api/question/QuestionTrue?${params}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getQuestion", error);
    return false;
  }
};

export const postQuestion = async (form) => {
  try {
    const response = await apiPost(`/api/question`, form);
    return response.data;
  } catch (error) {
    console.error("Ошибка в postQuestion", error);
    return false;
  }
};

export const patchQuestion = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/question/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchQuestion", error);
    return false;
  }
};

export const deleteQuestion = async (id) => {
  try {
    const response = await apiDelete(`/api/question/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteQuestion", error);
    return false;
  }
};

// ---------------------------------------------------------------------------------------------------------

export const getAnswers = async () => {
  try {
    const response = await apiGet(`/api/answers`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getAnswers", error);
    return false;
  }
};

export const getAnswersById = async (id) => {
  try {
    const response = await apiGet(`/api/answers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getAnswersById", error);
    return false;
  }
};

export const postAnswers = async (formdata) => {
  try {
    const response = await apiPost(`/api/answers`, formdata);
    return response.data;
  } catch (error) {
    console.error("Ошибка в postAnswers", error);
    return false;
  }
};

export const putStatusAnswer = async ({ id }) => {
  try {
    const response = await apiPut(`/api/answers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchAnswers", error);
    return false;
  }
};

export const patchAnswers = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/answers/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchAnswers", error);
    return false;
  }
};

export const deleteAnswers = async (id) => {
  try {
    const response = await apiDelete(`/api/answers/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteAnswers", error);
    return false;
  }
};

// ---------------------------------------------------------------------------------------------------------
// Обновленные API функции для работы с мультиязычными новостями

/**
 * Получить все новости
 * @param {string} lang - Код языка (ru, en, kz). Опционально для админки
 * @returns {Promise<Object|false>} - Объект с массивом новостей или false при ошибке
 */
export const getNews = async (lang) => {
  try {
    // Если язык передан, добавляем его как query параметр
    const url = lang ? `/api/news?lang=${lang}` : '/api/news';
    const response = await apiGet(url);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getNews", error);
    return false;
  }
};

/**
 * Создать новость
 * @param {FormData} formdata - FormData с данными новости
 * FormData должна содержать:
 * - date: строка с датой (обязательно)
 * - translations: JSON строка с переводами (обязательно)
 * - file: файл изображения (опционально)
 * @returns {Promise<Object|false>} - Созданная новость или false при ошибке
 */
export const postNews = async (formdata) => {
  try {
    const response = await apiPost(`/api/news`, formdata);
    return response.data;
  } catch (error) {
    console.error("Ошибка в postNews", error);
    return false;
  }
};

/**
 * Обновить новость
 * @param {Object} params
 * @param {number|string} params.id - ID новости
 * @param {FormData} params.formData - FormData с обновленными данными
 * FormData может содержать:
 * - date: новая дата (опционально)
 * - translations: JSON строка с обновленными переводами (опционально)
 * - file: новый файл изображения (опционально)
 * @returns {Promise<Object|false>} - Обновленная новость или false при ошибке
 */
export const patchNews = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/news/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchNews", error);
    return false;
  }
};

/**
 * Удалить новость
 * @param {number|string} id - ID новости
 * @returns {Promise<number|false>} - HTTP статус или false при ошибке
 */
export const deleteNews = async (id) => {
  try {
    const response = await apiDelete(`/api/news/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteNews", error);
    return false;
  }
};

/**
 * Получить новость по ID
 * @param {number|string} id - ID новости
 * @param {string} lang - Код языка (ru, en, kz). Опционально для админки
 * @returns {Promise<Object|false>} - Объект с данными новости или false при ошибке
 */
export const getNewsById = async (id, lang) => {
  try {
    // Если язык передан, добавляем его как query параметр
    const url = lang ? `/api/news/${id}?lang=${lang}` : `/api/news/${id}`;
    const response = await apiGet(url);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getNewsById", error);
    return false;
  }
};



// ---------------------------------------------------------------------------------------------------------
// ИСПРАВЛЕННЫЕ функции для Events API

export const getEvents = async () => {
  try {
    const response = await apiGet('/api/events'); // ✅ Исправлено: () вместо ``
    return response.data;
  } catch (error) {
    console.error("Ошибка в getEvents", error);
    return false;
  }
};

export const getEventById = async (id) => {
  try {
    const response = await apiGet(`/api/events/${id}`); // ✅ Исправлено: () вместо ``
    return response.data;
  } catch (error) {
    console.error("Ошибка в getEventById", error);
    return false;
  }
};

export const postEvents = async (formData) => {
  try {
    const response = await apiPost('/api/events', formData); // ✅ Исправлено: () вместо ``
    return response.data;
  } catch (error) {
    console.error("Ошибка в postEvents", error);
    return false;
  }
};

export const patchEvents = async (id, formData) => {
  try {
    const response = await apiPatch(`/api/events/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchEvents", error);
    return false;
  }
};

export const deleteEvents = async (id) => {
  try {
    const response = await apiDelete(`/api/events/${id}`); // ✅ Исправлено: () вместо ``
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteEvents", error);
    return false;
  }
};

// ---------------------------------------------------------------------------------------------------------

export const getTypes = async () => {
  try {
    const response = await apiGet(`/api/types`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getTypes", error);
    return false;
  }
};

export const postTypes = async (formsData) => {
  try {
    const response = await apiPost(`/api/types`, formsData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в postTypes", error);
    return false;
  }
};

export const patchTypes = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/types/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в patchTypes", error);
    return false;
  }
};

export const deleteTypes = async (id) => {
  try {
    const response = await apiDelete(`/api/types/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteTypes", error);
    return false;
  }
};

// ----------------------------------------------------------------------

export const getOrders = async () => {
  try {
    const response = await apiGet(`/api/orders`);
    return response.data;
  } catch (error) {
    console.error("Ошибка в getOrders", error);
    return false;
  }
};

export const createOrder = async (formData) => {
  try {
    const response = await apiPost(`/api/orders`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в createOrder", error);
    return false;
  }
};
export const updateOrder = async ({ id, formData }) => {
  try {
    const response = await apiPatch(`/api/orders/${id}`, formData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в updateOrder", error);
    return false;
  }
};
export const deleteOrder = async (id) => {
  try {
    const response = await apiDelete(`/api/orders/${id}`);
    return response.status;
  } catch (error) {
    console.error("Ошибка в deleteOrder", error);
    return false;
  }
};
// ----------------------------------------------------

export const sendDirectorMessage = async (messageData) => {
  try {
    const response = await apiPost(`/api/director/contact`, messageData);
    return response.data;
  } catch (error) {
    console.error("Ошибка в sendDirectorMessage", error);
    throw error;
  }
};